import { Button, Popconfirm, Table, Tooltip } from "antd";
import { EntityBase, PaginationRequest } from "../../entities/common";
import { DataGridProps } from "./types";
import { ColumnType } from "antd/lib/table";
import { TableRowSelection } from "antd/lib/table/interface";
import { useMemo } from "react";

export function DataGrid<TEntity extends EntityBase, TRequest extends PaginationRequest>({
  request,
  onRequestChange,
  data,
  columns,
  rowActions,
  rowKey = "id",
  selection,
  onSelectionChange,
  loading,
  pageSizeOptions,
}: DataGridProps<TEntity, TRequest>) {
  const actionColumn: ColumnType<TEntity> | undefined = useMemo(
    () =>
      rowActions?.length
        ? {
            title: "عملیات",
            key: "__actions",
            width: 100,
            fixed: "right",
            render: (_, record) => {
              const actions = rowActions?.filter((x) => !x.hidden?.(record)) ?? [];

              return (
                <div className="flex flex-wrap gap-1">
                  {actions.map((action) =>
                    action.confirm ? (
                      <Popconfirm
                        placement="topRight"
                        title={action.confirm.title}
                        okText="تایید"
                        cancelText="لغو"
                        okButtonProps={{ danger: action.danger, loading: action.loading?.(record) }}
                        onConfirm={() => action.onClick(record)}
                        disabled={action.disabled?.(record)}>
                        <Tooltip key={action.key} title={action.title}>
                          <Button size="small" danger icon={action.icon} />
                        </Tooltip>
                      </Popconfirm>
                    ) : (
                      <Tooltip key={action.key} title={action.title}>
                        <Button
                          size="small"
                          type="text"
                          danger={action.danger}
                          disabled={action.disabled?.(record)}
                          icon={action.icon}
                          onClick={() => action.onClick(record)}
                          loading={action.loading?.(record)}
                        />
                      </Tooltip>
                    ),
                  )}
                </div>
              );
            },
          }
        : undefined,
    [rowActions],
  );

  const finalColumns = useMemo(() => {
    if (!actionColumn) {
      return columns;
    }

    return [...(columns ?? []), actionColumn];
  }, [columns, actionColumn]);

  const rowSelection: TableRowSelection<TEntity> | undefined = onSelectionChange
    ? {
        selectedRowKeys: selection?.selectedRowKeys,
        onChange: (selectedRowKeys) => {
          onSelectionChange({
            selectedRowKeys,
          });
        },
        preserveSelectedRowKeys: true,
      }
    : undefined;

  return (
    <Table<TEntity>
      rowKey={rowKey as any}
      loading={loading}
      columns={finalColumns}
      dataSource={data?.items ?? []}
      pagination={{
        current: request.pageNumber,
        pageSize: request.pageSize,
        total: data?.pagination.totalCount,
        showTotal: (total) => `(تعداد کل: ${total})`,
        pageSizeOptions: pageSizeOptions ?? [10, 20, 50, 100, 1000, 10000, 100000, 1000000],
      }}
      onChange={(pagination, _, sorter) => {
        const sort = Array.isArray(sorter) ? sorter[0] : sorter;

        onRequestChange({
          ...request,
          pageNumber: pagination.current ?? 1,
          pageSize: pagination.pageSize ?? 20,
          sortBy: sort?.field?.toString(),
          sortDirection: sort?.order === "ascend" ? 0 : 1,
        });
      }}
      scroll={{ x: (finalColumns?.length ?? 1) * 90 + 320, y: "max(calc(100vh - 480px), 250px)" }}
      bordered
      sticky
      size="small"
      rowSelection={rowSelection}
    />
  );
}
